import React from 'react';
import 'twin.macro';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { Field, Form, FormSpy } from 'react-final-form';
import {
  CarnetDeNotes,
  CarnetElevesSort,
  Evaluation,
} from '@teetsh/app/src/containers/CarnetsDeNotes/types/carnetsDeNotes';
import AutoSubmitForm from '@teetsh/app/src/components/AutoSave';
import InputGroup from '@teetsh/app/src/components/Form/InputGroup';
import DateInput from '@teetsh/app/src/components/Form/Dateinput2';
import Notes from '@teetsh/app/src/containers/CarnetsDeNotes/Evaluations/components/Notes';
import { useUpdateEvaluation } from '@teetsh/app/src/containers/CarnetsDeNotes/queries/evaluations/useUpdateEvaluation';
import { Eleve } from '@teetsh/app/src/types/eleves';
import { Header } from '@teetsh/app/src/containers/CarnetsDeNotes/Evaluations/Header';
import { useEvaluation } from '@teetsh/app/src/containers/CarnetsDeNotes/queries/evaluations/useEvaluation';
import SpinnerComponent from '@teetsh/app/src/components/Spinner/ScreenSpinner';
import { DeleteNotesWarningModal } from './DeleteNotesWarningModal';
import { findElevesForGroups } from '@teetsh/app/src/containers/CarnetsDeNotes/CarnetDetails/findElevesForGroups';
import { GroupElevesPickerForm } from '@teetsh/app/src/containers/CarnetsDeNotes/Evaluations/GroupElevesPickerForm';
import { useOutletContext, useParams } from 'react-router-dom';
import { useUpdateCDN } from 'containers/CarnetsDeNotes/queries/useUpdateCDN';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useTranslatedValidation } from 'utils/useTranslatedValidation';
import { useCDNContext } from 'containers/CarnetsDeNotes/CarnetDeNotesContext';
import { UserLocale } from '@teetsh/app/src/types/user';
import { BoField } from './BoField';
import { getDistinctEleveGroups } from 'utils/getDistinctEleveGroups';
import { allGroupValue } from './constants';

function finalFormStatusToAutoSaveStatus(submitting: boolean, dirty: boolean) {
  if (submitting || dirty) {
    return 'saving';
  }
  return 'saved';
}

export function EvaluationForm() {
  const params = useParams<{
    evaluationId: string;
    carnetDeNotesId: string;
  }>();
  const { cdnId: carnetDeNotesId } = useCDNContext();
  const evaluationId = params.evaluationId as string;
  const updateCDN = useUpdateCDN();
  const { carnetDeNotes, eleves } = useOutletContext<{
    carnetDeNotes: CarnetDeNotes;
    eleves: Eleve[];
  }>();

  const onUpdateSort = (elevesSort: CarnetElevesSort) =>
    updateCDN({ ...carnetDeNotes, elevesSort });

  const { referencielId: boId, guestMode } = useUser();
  const { t } = useTranslation(['carnet', 'common']);
  const { required } = useTranslatedValidation();
  const { update: updateEvaluation } = useUpdateEvaluation(
    carnetDeNotesId,
    evaluationId,
  );
  const {
    data: evaluation,
    isLoading,
    isFetched,
  } = useEvaluation(carnetDeNotesId, evaluationId);

  if (isLoading || !isFetched || !evaluation) {
    return <SpinnerComponent />;
  }

  const groupList = getDistinctEleveGroups(
    eleves,
    evaluation.groups?.filter(g => g !== allGroupValue),
  );
  return (
    <React.Fragment key={evaluationId}>
      <DeleteNotesWarningModal />
      <Form<Evaluation>
        onSubmit={v => updateEvaluation(v)}
        initialValues={evaluation}
        subscription={{}}
        render={() => (
          <>
            <FormSpy subscription={{ submitting: true, dirty: true }}>
              {({ submitting, dirty }) => (
                <Header
                  status={finalFormStatusToAutoSaveStatus(submitting, dirty)}
                  isGuest={guestMode?.enabled}
                  carnetDeNotesId={carnetDeNotesId}
                  evaluationId={evaluation.id}
                />
              )}
            </FormSpy>
            <div tw="overflow-y-auto">
              <div tw="max-w-5xl pb-6 mx-auto w-full pt-2 md:pt-4 px-2 md:px-4">
                <div>
                  <AutoSubmitForm />
                  <div tw="md:flex mb-8 md:space-x-4">
                    <div tw="flex-grow mb-2 md:mb-0">
                      <Field
                        name="name"
                        render={({ input }) => (
                          <InputGroup
                            {...input}
                            id="name"
                            withoutMargin
                            placeholder={t(
                              'carnet:evaluationForm.titlePlaceholder',
                            )}
                            label={t('carnet:evaluationForm.title')}
                            extraLarge
                            autoFocus
                          />
                        )}
                      />
                    </div>
                    <div
                      css={css`
                        max-width: 15rem;
                      `}
                    >
                      <Field
                        name="date"
                        validate={required}
                        render={({
                          input: { value, onChange },
                          meta: { error, touched },
                        }) => (
                          <DateInput
                            userLocale={t('common:locale.long') as UserLocale}
                            extraLarge
                            error={touched && error}
                            label={t('carnet:evaluationForm.date')}
                            canBeNull
                            id="date"
                            value={value === '' ? null : value}
                            onChange={onChange}
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div tw="mb-6">
                    <BoField
                      boId={boId}
                      evaluationId={evaluationId}
                      carnetDeNotesId={carnetDeNotesId}
                      isGuest={guestMode?.enabled}
                      eleves={eleves}
                      notationSystem={carnetDeNotes.notationSystem}
                    />
                  </div>

                  <div tw="mb-4">
                    <Field
                      name="groups"
                      render={({ input: { value, onChange } }) => (
                        <div tw="flex">
                          <GroupElevesPickerForm
                            eleves={eleves}
                            evaluationId={evaluationId}
                            options={groupList}
                            value={value || []}
                            onChange={onChange}
                            notationSystem={carnetDeNotes.notationSystem}
                          />
                        </div>
                      )}
                    />
                  </div>

                  <div tw="mb-4">
                    <FormSpy subscription={{ values: true }}>
                      {({ values }) => (
                        <Notes
                          elevesSort={carnetDeNotes.elevesSort}
                          onUpdateSort={onUpdateSort}
                          eleves={findElevesForGroups(
                            eleves,
                            values.groups || [],
                          )}
                          onReplaceNotes={notes =>
                            updateEvaluation({
                              ...evaluation,
                              notes: notes.map(n => ({
                                ...n,
                                notationSystemMax:
                                  carnetDeNotes.notationSystem.maxValue,
                                notationSystemMin:
                                  carnetDeNotes.notationSystem.minValue,
                              })),
                            })
                          }
                          evaluation={evaluation}
                        />
                      )}
                    </FormSpy>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      />
    </React.Fragment>
  );
}
